// Daily Philosophy Tips for Profound Philosophy
// One tip surfaces per day on the dashboard (day-of-year rotation).
// Categories align with the eight learning modules.

const philosophyTips = [
  // --- Doing Philosophy (Module 1) ---
  {
    id: 1,
    category: "Doing Philosophy",
    tip: "Philosophy begins in wonder, not in answers. The Delphic oracle declared no one wiser than Socrates, and after questioning people reputed to be wise, he concluded that his only edge was this: what he did not know, he did not think he knew (Plato, Apology 21a to 21d). Comfort with open questions is a skill, and it strengthens with use.",
    actionable: "Catch one moment today where you're tempted to give a quick opinion. Hold the question open instead and ask what you'd need to know before answering."
  },
  {
    id: 2,
    category: "Doing Philosophy",
    tip: "The Socratic method isn't interrogation; it's cooperative digging. Each question peels back a layer: What do you mean? How do you know? What follows if that's true?",
    actionable: "In one conversation today, replace a statement with a genuine question. Ask 'what makes you say that?' and listen for the reasoning underneath the view."
  },
  {
    id: 3,
    category: "Doing Philosophy",
    tip: "A thought experiment is a laboratory you carry everywhere. Philosophers test ideas by imagining cases that isolate exactly one variable, with no equipment required.",
    actionable: "Take a belief you hold and invent a scenario where it's stretched to breaking point. Does the belief survive? What did the breaking point teach you?"
  },
  {
    id: 4,
    category: "Doing Philosophy",
    tip: "Strong thinkers steelman before they answer: they state the opposing view so well its supporters would applaud. Attacking a weak version of an argument teaches you nothing.",
    actionable: "Pick a view you disagree with and write two sentences putting its best case. Notice how much harder (and more honest) disagreement becomes."
  },

  // --- Logic & Reasoning (Module 2) ---
  {
    id: 5,
    category: "Logic & Reasoning",
    tip: "An argument is valid when the conclusion follows from the premises, and sound when the premises are also true. Plenty of persuasive arguments are neither, and separating the two is the core skill of logic.",
    actionable: "Next time you hear a confident claim, split it in two: is the reasoning tight, and are the starting facts actually true? Judge each separately."
  },
  {
    id: 6,
    category: "Logic & Reasoning",
    tip: "The ad hominem fallacy rejects an argument because of who made it. The messenger's flaws don't touch the logic: a hypocrite can still be right.",
    actionable: "Notice one moment today where a person's character is used to dismiss their point. Practise asking: would this argument stand if someone I admired had made it?"
  },
  {
    id: 7,
    category: "Logic & Reasoning",
    tip: "A false dilemma offers two options when more exist: 'you're either with us or against us'. Real decision spaces are almost always wider than the menu you're handed.",
    actionable: "When you face an either/or choice today, deliberately hunt for a third option. Even if you don't take it, finding it breaks the frame."
  },
  {
    id: 8,
    category: "Logic & Reasoning",
    tip: "Deduction guarantees its conclusions; induction only supports them. 'Every swan I've seen is white' never proves the next one will be, as Europe learnt when black swans turned up in Australia.",
    actionable: "Find one generalisation you rely on and ask how many cases it actually rests on. Would you bet a week's pay on the next instance?"
  },

  // --- Knowledge & Truth (Module 3) ---
  {
    id: 9,
    category: "Knowledge & Truth",
    tip: "An old analysis, often traced to Plato's Theaetetus, treats knowledge as justified true belief: it's not enough to be right; you need good reasons for being right. A lucky guess isn't knowledge.",
    actionable: "Take something you're sure of and ask what actually justifies it. Direct experience? A source you trust? A hunch that's never been tested?"
  },
  {
    id: 10,
    category: "Knowledge & Truth",
    tip: "Confirmation bias makes you a defence lawyer for your existing beliefs, collecting supporting evidence and cross-examining the rest. Knowing the bias exists isn't enough on its own; a deliberate process helps.",
    actionable: "Pick one belief you'd hate to lose and spend five minutes genuinely searching for the strongest evidence against it. Note what that feels like."
  },
  {
    id: 11,
    category: "Knowledge & Truth",
    tip: "Hume noticed that the sun rising every day of your life doesn't logically guarantee tomorrow's sunrise. Most of what you 'know' rests on induction: reliable, but never certain.",
    actionable: "Sort three of today's confident claims into bands: logically certain, overwhelmingly probable, or merely habitual. Watch how few land in the first band."
  },
  {
    id: 12,
    category: "Knowledge & Truth",
    tip: "Calibration means matching your confidence to your evidence. Being 90% sure should mean being wrong about one time in ten. Many people find, when they check, that they are more confident than their record supports.",
    actionable: "Before checking a fact today, write down how confident you are as a percentage. Then check. Repeat for a week and watch your calibration improve."
  },

  // --- Ethics (Module 4) ---
  {
    id: 13,
    category: "Ethics",
    tip: "Consequentialists judge actions by outcomes; deontologists by duties; virtue ethicists by character. Most moral disagreements are really disagreements about which lens is doing the looking.",
    actionable: "Take one moral judgement you made recently and run it through all three lenses. Do they agree? Where they split is where the interesting thinking lives."
  },
  {
    id: 14,
    category: "Ethics",
    tip: "Kant's test: act only on principles you could will everyone to follow. Lying works only because most people don't, and a universal exception collapses the very trust it exploits.",
    actionable: "Catch yourself making an exception for yourself today ('it's fine when I do it') and ask honestly: what happens if everyone in my position did the same?"
  },
  {
    id: 15,
    category: "Ethics",
    tip: "Aristotle located virtue between extremes: courage sits between cowardice and recklessness, generosity between meanness and waste. Character is built by repetition, not resolution: you become what you repeatedly do.",
    actionable: "Choose one virtue you want more of and find its two extremes. Then do one small, deliberate act today that lands between them."
  },
  {
    id: 16,
    category: "Ethics",
    tip: "The gap between stated values and lived behaviour is where ethical growth happens. Almost everyone values honesty, patience and generosity; the audit is in the calendar and the bank statement.",
    actionable: "Name your top three values, then check what yesterday's actual hours were spent on. Score the match honestly. No judgement, just data."
  },

  // --- Metaphysics (Module 5) ---
  {
    id: 17,
    category: "Metaphysics",
    tip: "The Ship of Theseus: replace every plank one by one: is it the same ship? Many of your cells turn over too. What makes anything, including you, the same thing across time?",
    actionable: "List three things that have changed completely about you in ten years, and one thread you think persisted. Ask what that thread actually is."
  },
  {
    id: 18,
    category: "Metaphysics",
    tip: "Determinism says every event, including your choices, follows from prior causes. Compatibilists reply that freedom isn't the absence of causes; it's acting from your own reasons rather than external compulsion.",
    actionable: "Watch one decision you make today and trace its causes: mood, habit, upbringing, deliberation. Does the tracing change how free the choice felt?"
  },
  {
    id: 19,
    category: "Metaphysics",
    tip: "Why is there something rather than nothing? It's the deepest question in metaphysics, and every answer (God, necessity, brute fact) carries its own puzzles. Sitting with it builds tolerance for genuine mystery.",
    actionable: "Give this question sixty undistracted seconds today. Notice whether your mind rushes to close it, and practise letting it stay open instead."
  },
  {
    id: 20,
    category: "Metaphysics",
    tip: "Persistence puzzles aren't academic: contracts, promises, punishment and gratitude all assume the person who acted is the person standing here now. Personal identity quietly underwrites your whole moral world.",
    actionable: "Think of a promise made years ago that still binds you. What exactly connects present-you to the person who made it: memory, body, character, or something else?"
  },

  // --- Mind & Consciousness (Module 6) ---
  {
    id: 21,
    category: "Mind & Consciousness",
    tip: "Chalmers' hard problem: explaining how the brain processes information still doesn't explain why any of it feels like something from the inside. Experience is the one datum you can't doubt and science can't yet touch.",
    actionable: "Pick one ordinary experience today (your first sip of coffee, rain on the window) and attend to what it's actually like, not what it's for."
  },
  {
    id: 22,
    category: "Mind & Consciousness",
    tip: "Searle's Chinese Room asks whether shuffling symbols by rule ever amounts to understanding them. Every chatbot conversation you have is a live rerun of this thought experiment.",
    actionable: "Next time software answers you fluently, ask what would count as evidence that it understands rather than simulates. Notice how hard the line is to draw."
  },
  {
    id: 23,
    category: "Mind & Consciousness",
    tip: "Qualia are the felt qualities of experience: the redness of red, the sting of cold water. Try fully describing one to someone who's never had it, and you'll discover the limits of language firsthand.",
    actionable: "Describe today's most vivid sensation in writing without naming it. What survives translation into words, and what stubbornly doesn't?"
  },
  {
    id: 24,
    category: "Mind & Consciousness",
    tip: "You never observe another mind, only behaviour, and you infer the rest. That inference is so automatic you forget it's there, yet it grounds all empathy and all doubt about machines.",
    actionable: "Watch one person today and notice the moment you 'read' their inner state. Ask what you actually saw versus what you inferred."
  },

  // --- Society & Justice (Module 7) ---
  {
    id: 25,
    category: "Society & Justice",
    tip: "Rawls' veil of ignorance: design society without knowing which position in it you'll occupy. Rules that survive that test are fair in a way rules built from your own vantage point rarely are.",
    actionable: "Take one policy opinion you hold and re-examine it as if you didn't know your income, health or postcode. Does the opinion move?"
  },
  {
    id: 26,
    category: "Society & Justice",
    tip: "Most political disagreement is philosophy in disguise: liberty versus equality, individual versus community, outcomes versus rights. Naming the underlying value clash cools the argument and sharpens it.",
    actionable: "Next political disagreement you witness, skip the topic and identify the two values colliding underneath. Say them out loud if the conversation allows."
  },
  {
    id: 27,
    category: "Society & Justice",
    tip: "The social contract tradition (Hobbes, Locke, Rousseau) asks why authority binds you at all. You never signed anything, yet you're governed. What, exactly, did you agree to by staying?",
    actionable: "List three rules you follow daily and ask of each: do I obey from agreement, habit, or fear of consequences? The honest answer varies more than expected."
  },
  {
    id: 28,
    category: "Society & Justice",
    tip: "Mill defended free expression with a wager. Silencing an opinion robs everyone: if it's true you lose the truth, and if it's false you lose the sharper grasp of truth that answering it builds.",
    actionable: "Read one article today from a source you usually avoid. Your goal isn't agreement; it's stating their strongest point accurately afterwards."
  },

  // --- Meaning & Existence (Module 8) ---
  {
    id: 29,
    category: "Meaning & Existence",
    tip: "Sartre's 'existence precedes essence': you weren't issued a purpose; you build one through choices, and every choice is also a small self-portrait. That freedom is exhilarating and heavy at once.",
    actionable: "Identify one thing you did today because 'that's just who I am'. Ask: is that essence, or a choice repeated so often it stopped looking like one?"
  },
  {
    id: 30,
    category: "Meaning & Existence",
    tip: "Bad faith, in Sartre's sense, is denying your own freedom: 'I had no choice' when you did, 'that's just how things are' when they aren't. It trades authorship for comfort.",
    actionable: "Catch one 'I had to' in your speech today and test it. What was the real alternative, and what did avoiding it cost or protect?"
  },
  {
    id: 31,
    category: "Meaning & Existence",
    tip: "Camus imagined Sisyphus happy: the boulder never stays up, and he pushes anyway, and the meaning is made in the pushing, not the summit. The absurd isn't defeated by answers; it's answered by revolt.",
    actionable: "Find the most repetitive task in your day and do it once with full attention, as if the doing itself were the point. Notice what changes."
  },
  {
    id: 32,
    category: "Meaning & Existence",
    tip: "Frankl, quoting Nietzsche in Man's Search for Meaning, wrote that whoever has a 'why' to live for can bear almost any 'how'. For Frankl, meaning is something you discover, and he named three ways: by creating a work or doing a deed, by experiencing something or loving someone, and by the stance you take towards suffering that can't be avoided.",
    actionable: "Write one sentence answering: where am I finding meaning right now? If the sentence comes hard, that's information worth having."
  }
];

// Get a tip of the day based on the current day
export const getTipOfTheDay = () => {
  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
  const tipIndex = dayOfYear % philosophyTips.length;
  return philosophyTips[tipIndex];
};

// Get a random tip
export const getRandomTip = () => {
  return philosophyTips[Math.floor(Math.random() * philosophyTips.length)];
};

// Get tips by category
export const getTipsByCategory = (category) => {
  return philosophyTips.filter(tip => tip.category === category);
};

// Get all categories
export const getTipCategories = () => {
  return [...new Set(philosophyTips.map(tip => tip.category))];
};

export default philosophyTips;
